import React, { createContext, useEffect, useContext } from 'react';
import { useSnapshot } from 'valtio';
import { sessionState, updateToken } from './sessionState';
import { GoogleOAuthProvider, GoogleLogin, useGoogleLogin } from '@react-oauth/google';
import { GoogleAuthSessionContext } from './googleAuthHooks';

// Google OAuth config
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'your-google-client-id';

// Wrapper component that provides Google OAuth context
export function GoogleAuthSessionProvider({ children }) {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <GoogleAuthSessionProviderInternal>
        {children}
      </GoogleAuthSessionProviderInternal>
    </GoogleOAuthProvider>
  );
}

// Internal component to handle the Google auth and token management
function GoogleAuthSessionProviderInternal({ children }) {
  const snapshot = useSnapshot(sessionState);

  // Handle successful Google login
  const handleGoogleSuccess = async (credentialResponse) => {
    const token = credentialResponse.credential || credentialResponse.access_token;
    if (token) {
      sessionState.isLoading = true;
      try {
        await updateToken(token);
      } catch (error) {
        console.error('Error processing Google authentication:', error);
      } finally {
        sessionState.isLoading = false;
      }
    } else {
        console.log('handleGoogleSuccess: no token received.');
    }
  };

  // Handle Google login error
  const handleGoogleError = (error) => {
    console.error('Google login error:', error);
    sessionState.isLoading = false;
  };

  // This programmatic login is for the auto-login attempt.
  const login = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  useEffect(() => {
    // On mount, if we don't have a token, we need to decide what to do.
    // The initial state is isLoading: true.
    if (!snapshot.token) {
        // After a delay, if we still have no token, we stop loading
        // so the UI can show the login button.
        const timer = setTimeout(() => {
            if (!sessionState.token) {
                sessionState.isLoading = false;
            }
        }, 2000); // Give 2s for any existing session to be found or auto-login to work.

        return () => clearTimeout(timer);
    }
    else {
        // If we do have a token, ensure loading is false.
        sessionState.isLoading = false;
    }
  }, []); // Run only once on component mount

  // Check if user is authenticated
  const isAuthenticated = !!snapshot.token;

  // If not authenticated and not loading, show Google Sign-In button
  if (!isAuthenticated && !snapshot.isLoading) {
    return (
      <GoogleAuthSessionContext.Provider value={{ session: snapshot }}>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              Sign in to continue
            </h2>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          </div>
        </div>
      </GoogleAuthSessionContext.Provider>
    );
  }

  // If authenticated, render children. The consuming component will handle the loading state.
  return (
    <GoogleAuthSessionContext.Provider value={{ session: snapshot }}>
      {children}
    </GoogleAuthSessionContext.Provider>
  );
}
